import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UploadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Upload,
  Modal,
  Form,
  Space,
  Tag,
  message,
  Descriptions,
  InputNumber,
  Input,
  Checkbox,
  Popconfirm,
  Typography,
  Divider,
  Empty,
  Radio,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';

const { Text } = Typography;
const { Option } = Select;

const STATUS_COLORS = {
  draft: 'default',
  reviewed: 'blue',
  approved: 'green',
  sent_to_absoft: 'purple',
  completed: 'success',
  cancelled: 'error',
};

export default function PayrollSubmissionsPanel({ companyId }) {
  const { t } = useTranslation(['tables', 'forms']);
  const queryClient = useQueryClient();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm] = Form.useForm();

  // Upload modal state
  const [sourceType, setSourceType] = useState('DREAM_POINT'); // DREAM_POINT or PCCC
  const [emlFiles, setEmlFiles] = useState([]);
  const [abSoftFiles, setAbSoftFiles] = useState([]);
  const [uploadForm] = Form.useForm();

  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const STATUS_LABELS = {
    draft: pa('statusDraft'),
    reviewed: pa('statusReviewed'),
    approved: pa('statusApproved'),
    sent_to_absoft: pa('statusSentToAbsoft'),
    completed: pa('statusCompleted'),
    cancelled: pa('statusCancelled'),
  };

  // Fetch submissions
  const { data: submissionsData, isLoading } = useQuery({
    queryKey: ['payroll-submissions', companyId],
    queryFn: () =>
      API.payrollAutomation.getSubmissions({ companyId, limit: 50 }),
    enabled: !!companyId,
  });

  const submissions = submissionsData?.data || [];

  // Fetch submission details
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['payroll-submission-detail', selectedSubmission?._id],
    queryFn: () =>
      API.payrollAutomation.getSubmissionById(selectedSubmission._id),
    enabled: !!selectedSubmission?._id && detailModalVisible,
  });

  const submissionDetail = detailData?.data || selectedSubmission;

  // Upload mutation (Dream Point - EML only)
  const uploadMutation = useMutation({
    mutationFn: (formData) => API.payrollAutomation.uploadEmlFile(formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      setUploadModalVisible(false);
      resetUploadModal();
      message.success(pa('emlUploadSuccess'));
      setSelectedSubmission(response.data);
      setDetailModalVisible(true);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // PCCC upload mutation (EML + Excel files)
  const uploadPCCCMutation = useMutation({
    mutationFn: (formData) => API.payrollAutomation.uploadPCCCFiles(formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      setUploadModalVisible(false);
      resetUploadModal();
      message.success(pa('pcccUploadSuccess'));
      setSelectedSubmission(response.data);
      setDetailModalVisible(true);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // CCECC upload mutation (EML + Excel files with PDF attachments)
  // Response now returns an array of submissions (one per HLB company)
  const uploadCCECCMutation = useMutation({
    mutationFn: (formData) => API.payrollAutomation.uploadCCECCFiles(formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      setUploadModalVisible(false);
      resetUploadModal();
      const submissions = Array.isArray(response.data) ? response.data : [response.data];
      const totalEmployees = submissions.reduce((sum, s) => sum + (s.employees?.length || 0), 0);
      message.success(
        `${submissions.length} submission(s) created — ${totalEmployees} employees total`
      );
      // Open detail for the first submission
      if (submissions.length > 0) {
        setSelectedSubmission(submissions[0]);
        setDetailModalVisible(true);
      }
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: ({ submissionId, status }) =>
      API.payrollAutomation.changeSubmissionStatus(submissionId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      queryClient.invalidateQueries(['payroll-submission-detail']);
      message.success(pa('statusUpdated'));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Update employee mutation
  const updateEmployeeMutation = useMutation({
    mutationFn: ({ submissionId, employeeId, data }) =>
      API.payrollAutomation.updateEmployeeInSubmission(
        submissionId,
        employeeId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(['payroll-submission-detail']);
      setEditingEmployee(null);
      message.success(pa('employeeUpdated'));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Remove employee from submission mutation
  const removeEmployeeMutation = useMutation({
    mutationFn: ({ submissionId, employeeId }) =>
      API.payrollAutomation.removeEmployeeFromSubmission(
        submissionId,
        employeeId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(['payroll-submission-detail']);
      message.success(pa('employeeRemoved'));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Delete submission mutation
  const deleteMutation = useMutation({
    mutationFn: (submissionId) =>
      API.payrollAutomation.deleteSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      message.success(pa('submissionDeleted'));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Export submission mutation
  const exportMutation = useMutation({
    mutationFn: (submissionId) =>
      API.payrollAutomation.exportSubmissionToExcel(submissionId),
    onSuccess: () => {
      message.success(pa('exportSuccess'));
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Create timesheets mutation
  const createTimesheetsMutation = useMutation({
    mutationFn: (submissionId) =>
      API.payrollAutomation.createTimesheetsFromSubmission(submissionId),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['payroll-submission-detail']);
      queryClient.invalidateQueries(['payroll-submissions', companyId]);
      const {data} = response;
      message.success(
        `${pa('timesheetCreationSuccess')}: ${data.createdCount} created, ${data.skippedCount} skipped`
      );
    },
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
    },
  });

  // Reset upload modal state
  const resetUploadModal = () => {
    setSourceType('DREAM_POINT');
    setEmlFiles([]);
    setAbSoftFiles([]);
    uploadForm.resetFields();
  };

  // Handle Dream Point EML upload (immediate upload on file select)
  const handleDreamPointUpload = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    uploadMutation.mutate(formData);
    return false; // Prevent default upload behavior
  };

  // Handle PCCC upload submit (triggered by button click)
  const handlePCCCUploadSubmit = async () => {
    try {
      const values = await uploadForm.validateFields();

      if (emlFiles.length === 0) {
        message.error(pa('emlFilesRequired'));
        return;
      }

      if (abSoftFiles.length === 0) {
        message.error(pa('abSoftFilesRequired'));
        return;
      }

      const formData = new FormData();
      emlFiles.forEach((file) => {
        formData.append('emlFiles', file);
      });
      abSoftFiles.forEach((file) => {
        formData.append('abSoftFiles', file);
      });
      formData.append('companyId', companyId);
      formData.append('month', values.month);
      formData.append('year', values.year);
      formData.append('parserType', 'PCCC_PARSER'); // Send parser type to backend
      if (values.projectCode) {
        formData.append('projectCode', values.projectCode.toUpperCase());
      }

      uploadPCCCMutation.mutate(formData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // Handle CCECC upload submit (AB Soft files are optional)
  const handleCCECCUploadSubmit = async () => {
    try {
      const values = await uploadForm.validateFields();

      if (emlFiles.length === 0) {
        message.error(pa('emlFilesRequired'));
        return;
      }

      const formData = new FormData();
      emlFiles.forEach((file) => {
        formData.append('emlFiles', file);
      });
      abSoftFiles.forEach((file) => {
        formData.append('abSoftFiles', file);
      });
      formData.append('companyId', companyId);
      formData.append('month', values.month);
      formData.append('year', values.year);

      uploadCCECCMutation.mutate(formData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleViewDetails = (submission) => {
    setSelectedSubmission(submission);
    setDetailModalVisible(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    employeeForm.setFieldsValue({
      name: employee.name,
      surname: employee.surname,
      hoursWorked: employee.hoursWorked,
      workedOnHoliday: employee.workedOnHoliday,
      vacationDays: employee.vacationDays,
      sickLeaveDays: employee.sickLeaveDays,
      maternityLeave: employee.maternityLeave,
      netSalary: employee.netSalary,
      mealAllowance: employee.mealAllowance,
      regres: employee.regres,
      abSoftCode: employee.abSoftCode,
      notes: employee.notes,
      reviewed: employee.reviewed,
    });
  };

  const handleSaveEmployee = async () => {
    try {
      const values = await employeeForm.validateFields();
      updateEmployeeMutation.mutate({
        submissionId: submissionDetail._id,
        employeeId: editingEmployee._id,
        data: values,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleStatusChange = (submissionId, newStatus) => {
    statusMutation.mutate({ submissionId, status: newStatus });
  };

  // Submissions table columns
  const columns = [
    {
      title: pa('period'),
      key: 'period',
      render: (_, record) => `${record.month}/${record.year}`,
      width: 100,
    },
    {
      title: pa('hlbCompanyName'),
      dataIndex: 'hlbCompany',
      key: 'hlbCompany',
      width: 180,
      render: (val) => (val?.name ? <Tag color="blue">{val.name}</Tag> : '-'),
    },
    {
      title: pa('projectCode'),
      dataIndex: 'projectCode',
      key: 'projectCode',
      width: 200,
      render: (val) => (val ? <Tag color="orange">{val}</Tag> : '-'),
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
      width: 140,
    },
    {
      title: t('createdAt', { ns: 'tables' }),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      width: 150,
    },
    {
      title: pa('from'),
      key: 'from',
      render: (_, record) => record.emailMetadata?.originalFrom || '-',
      ellipsis: true,
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
          {['draft', 'cancelled'].includes(record.status) && (
            <Popconfirm
              title={pa('deleteSubmission')}
              onConfirm={() => deleteMutation.mutate(record._id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Employee table columns for detail view
  const employeeColumns = [
    {
      title: t('name', { ns: 'tables' }),
      key: 'name',
      render: (_, record) => `${record.name} ${record.surname || ''}`,
    },
    {
      title: pa('hoursWorked'),
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      width: 80,
    },
    {
      title: pa('workedOnHoliday'),
      dataIndex: 'workedOnHoliday',
      key: 'workedOnHoliday',
      width: 80,
      render: (val) =>
        val ? (
          <Tag color="orange">{t('common.yes', { ns: 'forms' })}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: pa('vacationDays'),
      dataIndex: 'vacationDays',
      key: 'vacationDays',
      width: 80,
      render: (val) => (val > 0 ? <Tag color="blue">{val}d</Tag> : '-'),
    },
    {
      title: pa('abSoftCode'),
      dataIndex: 'abSoftCode',
      key: 'abSoftCode',
      width: 80,
    },
    ...(submissionDetail?.sourceType === 'PCCC'
      ? [
          {
            title: pa('matching'),
            key: 'matching',
            width: 150,
            render: (_, record) => {
              if (!record.matchMethod) return '-';

              const matchColors = {
                JMBG: 'green',
                FUZZY_NAME: 'blue',
                PROJECT_NAME: 'orange',
                NO_MATCH: 'red',
              };

              return (
                <div>
                  <Tag
                    color={matchColors[record.matchMethod] || 'default'}
                    style={{ marginBottom: 4 }}
                  >
                    {record.matchMethod?.replace(/_/g, ' ')}
                  </Tag>
                  {record.matchScore > 0 && (
                    <div style={{ fontSize: 11, color: '#666' }}>
                      {record.matchScore}% confidence
                    </div>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    ...(submissionDetail?.sourceType === 'CCECC'
      ? [
          {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            width: 110,
            render: (val) => (val ? <Tag color="cyan">{val}</Tag> : '-'),
          },
          {
            title: 'Data Sources',
            dataIndex: 'dataSources',
            key: 'dataSources',
            width: 200,
            render: (sources) => {
              if (!sources || sources.length === 0) return '-';
              const sourceColors = {
                masterExcel: 'blue',
                attendance: 'green',
                overtimePDF: 'orange',
                travelExpensesPDF: 'purple',
                leaveDecisionPDF: 'cyan',
                sickLeavePDF: 'red',
                leaveFormPDF: 'magenta',
              };
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {sources.map((src, i) => (
                    <Tag
                      key={i}
                      color={sourceColors[src] || 'default'}
                      style={{ fontSize: 10, margin: 0 }}
                    >
                      {src.replace(/PDF$/, '').replace(/Excel$/, '')}
                    </Tag>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: pa('notes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: pa('reviewed'),
      dataIndex: 'reviewed',
      key: 'reviewed',
      width: 80,
      render: (val) =>
        val ? (
          <CheckOutlined style={{ color: 'green' }} />
        ) : (
          <CloseOutlined style={{ color: '#ccc' }} />
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size={0}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditEmployee(record)}
            disabled={!['draft', 'reviewed'].includes(submissionDetail?.status)}
          />
          <Popconfirm
            title={pa('removeEmployeeConfirm')}
            onConfirm={() =>
              removeEmployeeMutation.mutate({
                submissionId: submissionDetail._id,
                employeeId: record._id,
              })
            }
            disabled={!['draft', 'reviewed'].includes(submissionDetail?.status)}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<CloseOutlined />}
              disabled={
                !['draft', 'reviewed'].includes(submissionDetail?.status)
              }
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!companyId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={pa('selectCompanyPrompt')}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>{pa('payrollSubmissions')}</h3>
          <Text type="secondary">{pa('uploadAndReview')}</Text>
        </div>
        <button
          className="btn-primary"
          onClick={() => setUploadModalVisible(true)}
        >
          <UploadOutlined /> {pa('uploadEml')}
        </button>
      </div>

      {/* Submissions Table */}
      <Table
        columns={columns}
        dataSource={submissions}
        rowKey="_id"
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
      />

      {/* Upload Modal */}
      <Modal
        title={pa('uploadPayrollData')}
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          resetUploadModal();
        }}
        footer={null}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          {/* Source Type Selection */}
          <div style={{ marginBottom: 24 }}>
            <Text strong>{pa('selectSourceType')}</Text>
            <Radio.Group
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              style={{ marginTop: 8, display: 'block' }}
            >
              <Space direction="vertical" size={12}>
                <Radio value="DREAM_POINT">
                  <div>
                    <div>
                      <Text strong>Dream Point Distribution</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {pa('dreamPointDesc')}
                    </Text>
                  </div>
                </Radio>
                <Radio value="PCCC">
                  <div>
                    <div>
                      <Text strong>PCCC (China Power)</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {pa('pcccDesc')}
                    </Text>
                  </div>
                </Radio>
                <Radio value="CCECC">
                  <div>
                    <div>
                      <Text strong>CCECC (China Civil Engineering)</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      EML files with Excel + PDF attachments (overtime, leave,
                      travel) + AB Soft files
                    </Text>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          {/* Dream Point Upload Flow */}
          {sourceType === 'DREAM_POINT' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Upload.Dragger
                accept=".eml,.msg"
                beforeUpload={handleDreamPointUpload}
                showUploadList={false}
                disabled={uploadMutation.isPending}
              >
                <p className="ant-upload-drag-icon">
                  <FileTextOutlined
                    style={{ fontSize: 48, color: '#1890ff' }}
                  />
                </p>
                <p className="ant-upload-text">{pa('dragDropEml')}</p>
                <p className="ant-upload-hint">{pa('emlParseHint')}</p>
              </Upload.Dragger>
              {uploadMutation.isPending && (
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">{pa('parsingEmail')}</Text>
                </div>
              )}
            </div>
          )}

          {/* PCCC Upload Flow */}
          {sourceType === 'PCCC' && (
            <div>
              <Form
                form={uploadForm}
                layout="vertical"
                initialValues={{ year: new Date().getFullYear() }}
              >
                {/* EML Files Upload (Multiple) */}
                <Form.Item
                  label={pa('emlFiles')}
                  required
                  tooltip={pa('emlFilesTooltip')}
                >
                  <Upload
                    accept=".eml,.msg"
                    multiple
                    beforeUpload={(file) => {
                      setEmlFiles((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setEmlFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                    }}
                    fileList={emlFiles}
                  >
                    <Button icon={<UploadOutlined />}>
                      {pa('selectEmlFiles')}
                    </Button>
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pa('emlFilesHint')}
                  </Text>
                </Form.Item>

                {/* AB Soft Excel Files Upload */}
                <Form.Item
                  label={pa('abSoftFiles')}
                  required
                  tooltip={pa('abSoftFilesTooltip')}
                >
                  <Upload
                    accept=".xlsx,.xls"
                    multiple
                    beforeUpload={(file) => {
                      setAbSoftFiles((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setAbSoftFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                    }}
                    fileList={abSoftFiles}
                  >
                    <Button icon={<UploadOutlined />}>
                      {pa('selectAbSoftFiles')}
                    </Button>
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pa('abSoftFilesHint')}
                  </Text>
                </Form.Item>

                {/* Month Selection */}
                <Form.Item
                  name="month"
                  label={pa('month')}
                  rules={[
                    { required: true, message: t('required', { ns: 'forms' }) },
                  ]}
                >
                  <Select placeholder={pa('selectMonth')}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {dayjs().month(i).format('MMMM')}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Year Dropdown */}
                <Form.Item
                  name="year"
                  label={pa('year')}
                  rules={[
                    { required: true, message: t('required', { ns: 'forms' }) },
                  ]}
                >
                  <Select placeholder={pa('selectYear')}>
                    {Array.from({ length: 7 }, (_, i) => {
                      const year = new Date().getFullYear() - 5 + i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                {/* Project Code */}
                <Form.Item
                  name="projectCode"
                  label={pa('projectCode')}
                  tooltip={pa('projectCodeTooltip')}
                >
                  <Input
                    placeholder="METRO_L1P1D"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>

                {/* Submit Button */}
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    className="btn-primary"
                    block
                    onClick={handlePCCCUploadSubmit}
                    loading={uploadPCCCMutation.isPending}
                    disabled={emlFiles.length === 0 || abSoftFiles.length === 0}
                  >
                    {pa('uploadAndParse')}
                  </Button>
                  {uploadPCCCMutation.isPending && (
                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                      <Text type="secondary">{pa('parsingPCCCFiles')}</Text>
                    </div>
                  )}
                </Form.Item>
              </Form>
            </div>
          )}

          {/* CCECC Upload Flow */}
          {sourceType === 'CCECC' && (
            <div>
              <Form
                form={uploadForm}
                layout="vertical"
                initialValues={{ year: new Date().getFullYear() }}
              >
                {/* EML Files Upload (Multiple) */}
                <Form.Item
                  label="EML / MSG Files"
                  required
                  tooltip="EML or Outlook MSG files containing Excel and PDF attachments from CCECC"
                >
                  <Upload
                    accept=".eml,.msg"
                    multiple
                    beforeUpload={(file) => {
                      setEmlFiles((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setEmlFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                    }}
                    fileList={emlFiles}
                  >
                    <Button icon={<UploadOutlined />}>Select EML Files</Button>
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Upload EML files with Excel (master list, attendance) and
                    PDF (overtime, leave, travel) attachments
                  </Text>
                </Form.Item>

                {/* AB Soft Excel Files Upload (Optional) */}
                <Form.Item
                  label={pa('abSoftOptional')}
                  tooltip={pa('abSoftFilesTooltip')}
                >
                  <Upload
                    accept=".xlsx,.xls"
                    multiple
                    beforeUpload={(file) => {
                      setAbSoftFiles((prev) => [...prev, file]);
                      return false;
                    }}
                    onRemove={(file) => {
                      setAbSoftFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                    }}
                    fileList={abSoftFiles}
                  >
                    <Button icon={<UploadOutlined />}>
                      {pa('selectAbSoftFiles')}
                    </Button>
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pa('abSoftOptionalHint')}
                  </Text>
                </Form.Item>

                {/* Month Selection */}
                <Form.Item
                  name="month"
                  label={pa('month')}
                  rules={[
                    { required: true, message: t('required', { ns: 'forms' }) },
                  ]}
                >
                  <Select placeholder={pa('selectMonth')}>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {dayjs().month(i).format('MMMM')}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Year Dropdown */}
                <Form.Item
                  name="year"
                  label={pa('year')}
                  rules={[
                    { required: true, message: t('required', { ns: 'forms' }) },
                  ]}
                >
                  <Select placeholder={pa('selectYear')}>
                    {Array.from({ length: 7 }, (_, i) => {
                      const year = new Date().getFullYear() - 5 + i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                {/* Submit Button */}
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    className="btn-primary"
                    block
                    onClick={handleCCECCUploadSubmit}
                    loading={uploadCCECCMutation.isPending}
                    disabled={emlFiles.length === 0}
                  >
                    Upload & Parse CCECC Files
                  </Button>
                  {uploadCCECCMutation.isPending && (
                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                      <Text type="secondary">
                        Parsing CCECC files (Excel + PDF)... This may take up to
                        2 minutes.
                      </Text>
                    </div>
                  )}
                </Form.Item>
              </Form>
            </div>
          )}
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${pa('submissionPeriod')}: ${submissionDetail?.month}/${submissionDetail?.year}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedSubmission(null);
          setEditingEmployee(null);
        }}
        width={1400}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space direction="vertical" size={4} align="start">
              <Space>
                <Button
                  className="btn-secondary"
                  icon={<DownloadOutlined />}
                  onClick={() => exportMutation.mutate(submissionDetail._id)}
                  loading={exportMutation.isPending}
                  disabled={!submissionDetail?.employees?.length}
                >
                  {submissionDetail?.sourceType === 'PCCC'
                    ? pa('exportByWorkUnits')
                    : pa('exportToExcel')}
                </Button>
                {submissionDetail?.timesheetCreation?.status !== 'completed' &&
                  submissionDetail?.employees?.some((e) => e.employeeRef) && (
                    <Popconfirm
                      title={pa('createTimesheetsConfirm')}
                      onConfirm={() =>
                        createTimesheetsMutation.mutate(submissionDetail._id)
                      }
                      okText={pa('createTimesheets')}
                      cancelText={pa('cancel')}
                    >
                      <Button
                        type="primary"
                        className="btn-primary"
                        icon={<FileTextOutlined />}
                        loading={createTimesheetsMutation.isPending}
                      >
                        {pa('createTimesheets')}
                      </Button>
                    </Popconfirm>
                  )}
                {submissionDetail?.timesheetCreation?.status === 'completed' && (
                  <Tag color="success">{pa('timesheetsAlreadyCreated')}</Tag>
                )}
              </Space>
              {submissionDetail?.sourceType === 'PCCC' && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {pa('pcccExportHint')}
                </Text>
              )}
              {submissionDetail?.timesheetCreation?.status === 'completed' && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {pa('timesheetsCreated', {
                    count: submissionDetail.timesheetCreation.createdCount || 0,
                  })}
                  {' | '}
                  {pa('timesheetsSkipped', {
                    count: submissionDetail.timesheetCreation.skippedCount || 0,
                  })}
                </Text>
              )}
            </Space>
            <Space>
              {submissionDetail?.status === 'draft' && (
                <Button
                  type="primary"
                  className="btn-primary"
                  onClick={() =>
                    handleStatusChange(submissionDetail._id, 'reviewed')
                  }
                  loading={statusMutation.isPending}
                >
                  {pa('markAsReviewed')}
                </Button>
              )}
              {submissionDetail?.status === 'reviewed' && (
                <>
                  <Button
                    className="btn-secondary"
                    onClick={() =>
                      handleStatusChange(submissionDetail._id, 'draft')
                    }
                  >
                    {pa('backToDraft')}
                  </Button>
                  <Button
                    type="primary"
                    className="btn-primary"
                    onClick={() =>
                      handleStatusChange(submissionDetail._id, 'approved')
                    }
                    loading={statusMutation.isPending}
                  >
                    {pa('markAsApproved')}
                  </Button>
                </>
              )}
              {submissionDetail?.status === 'approved' && (
                <>
                  <Button
                    className="btn-secondary"
                    onClick={() =>
                      handleStatusChange(submissionDetail._id, 'reviewed')
                    }
                  >
                    {pa('backToReviewed')}
                  </Button>
                  <Button
                    type="primary"
                    className="btn-primary"
                    onClick={() =>
                      handleStatusChange(submissionDetail._id, 'sent_to_absoft')
                    }
                    loading={statusMutation.isPending}
                  >
                    {pa('markAsSentToAbsoft')}
                  </Button>
                </>
              )}
              {submissionDetail?.status === 'sent_to_absoft' && (
                <Button
                  type="primary"
                  className="btn-primary"
                  onClick={() =>
                    handleStatusChange(submissionDetail._id, 'completed')
                  }
                  loading={statusMutation.isPending}
                >
                  {pa('markAsCompleted')}
                </Button>
              )}
            </Space>
          </div>
        }
      >
        {detailLoading ? (
          <div>{t('loading', { ns: 'tables' })}</div>
        ) : submissionDetail ? (
          <>
            {/* Submission Info */}
            <Descriptions size="small" column={3} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={pa('sourceType')}>
                <Tag
                  color={
                    submissionDetail.sourceType === 'PCCC'
                      ? 'purple'
                      : submissionDetail.sourceType === 'CCECC'
                        ? 'volcano'
                        : 'blue'
                  }
                >
                  {submissionDetail.sourceType === 'PCCC'
                    ? 'PCCC (China Power)'
                    : submissionDetail.sourceType === 'CCECC'
                      ? 'CCECC (China Civil Engineering)'
                      : 'Dream Point'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('status', { ns: 'tables' })}>
                <Tag color={STATUS_COLORS[submissionDetail.status]}>
                  {STATUS_LABELS[submissionDetail.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={pa('period')}>
                {submissionDetail.month}/{submissionDetail.year}
              </Descriptions.Item>
              {submissionDetail.hlbCompany && (
                <Descriptions.Item label={pa('hlbCompanyName')}>
                  <Tag color="blue">{submissionDetail.hlbCompany.name}</Tag>
                </Descriptions.Item>
              )}
              {submissionDetail.sourceType === 'PCCC' &&
                submissionDetail.projectCode && (
                  <Descriptions.Item label={pa('projectCode')}>
                    <Tag color="orange">{submissionDetail.projectCode}</Tag>
                  </Descriptions.Item>
                )}
              <Descriptions.Item label={pa('employees')}>
                {submissionDetail.employees?.length || 0}
              </Descriptions.Item>
              <Descriptions.Item label={pa('confidence')}>
                <Tag
                  color={
                    submissionDetail.parsingInfo?.confidence >= 80
                      ? 'green'
                      : submissionDetail.parsingInfo?.confidence >= 60
                        ? 'orange'
                        : 'red'
                  }
                >
                  {submissionDetail.parsingInfo?.confidence || 0}%
                </Tag>
              </Descriptions.Item>
              {submissionDetail.sourceType === 'PCCC' &&
                submissionDetail.parsingInfo?.matchingStats && (
                  <Descriptions.Item label={pa('employeeMatching')} span={3}>
                    <Space>
                      <Tag color="green">
                        JMBG:{' '}
                        {submissionDetail.parsingInfo.matchingStats
                          .jmbgMatches || 0}
                      </Tag>
                      <Tag color="blue">
                        Fuzzy:{' '}
                        {submissionDetail.parsingInfo.matchingStats
                          .fuzzyMatches || 0}
                      </Tag>
                      <Tag color="orange">
                        Project:{' '}
                        {submissionDetail.parsingInfo.matchingStats
                          .projectMatches || 0}
                      </Tag>
                      <Tag color="red">
                        No Match:{' '}
                        {submissionDetail.parsingInfo.matchingStats.noMatches ||
                          0}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        (AB Soft:{' '}
                        {submissionDetail.parsingInfo.abSoftEmployeeCount || 0}{' '}
                        employees)
                      </Text>
                    </Space>
                  </Descriptions.Item>
                )}
              <Descriptions.Item label={pa('from')}>
                {submissionDetail.emailMetadata?.originalFrom || '-'}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('subject', { ns: 'tables' })}
                span={2}
              >
                {submissionDetail.emailMetadata?.originalSubject || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* PCCC Attachments - Excel Files */}
            {submissionDetail.sourceType === 'PCCC' &&
              submissionDetail.attachments?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {pa('attachedExcelFiles')} (
                    {submissionDetail.attachments.length})
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {submissionDetail.attachments.map((attachment, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: 4,
                          padding: '8px 12px',
                          backgroundColor: '#fafafa',
                          flex: '1 1 300px',
                          maxWidth: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div>
                              <FileTextOutlined
                                style={{ marginRight: 6, color: '#52c41a' }}
                              />
                              <Text strong style={{ fontSize: 13 }}>
                                {attachment.filename}
                              </Text>
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <Tag color="blue" style={{ fontSize: 11 }}>
                                {attachment.fileType?.replace(/_/g, ' ') ||
                                  'OTHER'}
                              </Tag>
                              {attachment.parseConfidence && (
                                <Tag
                                  color={
                                    attachment.parseConfidence >= 80
                                      ? 'green'
                                      : attachment.parseConfidence >= 60
                                        ? 'orange'
                                        : 'red'
                                  }
                                  style={{ fontSize: 11 }}
                                >
                                  {attachment.parseConfidence}%
                                </Tag>
                              )}
                            </div>
                          </div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 11,
                              whiteSpace: 'nowrap',
                              marginLeft: 8,
                            }}
                          >
                            {attachment.fileSize
                              ? `${(attachment.fileSize / 1024).toFixed(1)} KB`
                              : '-'}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Parsing Warnings */}
            {submissionDetail.parsingInfo?.warnings?.length > 0 && (
              <div style={{ marginBottom: 16, maxWidth: '100%' }}>
                {submissionDetail.parsingInfo.warnings.map((warning, idx) => (
                  <Tag
                    color="warning"
                    key={idx}
                    style={{
                      marginBottom: 8,
                      display: 'inline-block',
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      height: 'auto',
                    }}
                  >
                    {warning}
                  </Tag>
                ))}
              </div>
            )}

            <Divider />

            {/* Employees Table */}
            <Table
              columns={employeeColumns}
              dataSource={submissionDetail.employees || []}
              rowKey="_id"
              pagination={false}
              size="small"
            />

            {/* Edit Employee Form */}
            {editingEmployee && (
              <>
                <Divider>
                  {pa('editEmployee')}: {editingEmployee.name}{' '}
                  {editingEmployee.surname}
                </Divider>
                <Form form={employeeForm} layout="vertical">
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 16,
                    }}
                  >
                    <Form.Item name="name" label={t('name', { ns: 'tables' })}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="surname" label={pa('surname')}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="abSoftCode" label={pa('abSoftCode')}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="hoursWorked" label={pa('hoursWorked')}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="vacationDays" label={pa('vacationDays')}>
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="sickLeaveDays" label={pa('sickLeaveDays')}>
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="netSalary" label={pa('netSalary')}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="mealAllowance" label={pa('mealAllowance')}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="regres" label={pa('regres')}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item name="workedOnHoliday" valuePropName="checked">
                      <Checkbox>{pa('workedOnHoliday')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="maternityLeave" valuePropName="checked">
                      <Checkbox>{pa('maternityLeave')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="reviewed" valuePropName="checked">
                      <Checkbox>{pa('reviewed')}</Checkbox>
                    </Form.Item>
                  </div>
                  <Form.Item name="notes" label={pa('notes')}>
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Space>
                    <Button
                      className="btn-secondary"
                      onClick={() => setEditingEmployee(null)}
                    >
                      {pa('cancel')}
                    </Button>
                    <Button
                      type="primary"
                      className="btn-primary"
                      onClick={handleSaveEmployee}
                      loading={updateEmployeeMutation.isPending}
                    >
                      {pa('save')}
                    </Button>
                  </Space>
                </Form>
              </>
            )}
          </>
        ) : null}
      </Modal>
    </div>
  );
}
